import { useCallback, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router';
import { colors } from 'ui/theme';
import { Skeleton } from 'ui/skeleton';
import { User16 } from 'ui/icons/aurora/16px/studio';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { Badge } from 'ui/badge';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { DisplayPriorityCampaign } from 'modules/placements/types';
import { DATE_FORMAT_FULL_WITH_HOURS_MINS, displayDate, stripTimezonePrefix } from 'utils/date';
import { getLabelForDisplayUntilForJourneyCampaign } from 'modules/placements/utils';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import { Eligibility16, Hide16, ToBottom24, ToTop24 } from 'ui/icons';
import { TriggerWorkflow24 } from 'ui/icons/aurora/24px/studio';
import { View16 } from 'ui/icons/aurora';
import { Tooltip } from 'ui/tooltip';
import { useCampaignSendSize, useCampaignSendSizeData } from 'modules/campaigns/lib';
import { useIsModernizedSegmentationPageEnabled } from 'modules/segmentation/hooks';
import {
  MessageMediumIcon,
  StyledImageWrapper,
} from 'modules/campaigns/lib/components/CampaignPreviewThumbnail';
import { getCampaignStateBadgeVariant } from 'modules/campaigns/lib/utils';
import {
  CampaignDetails,
  BaseDetails,
  OrderElementWrapper,
  StyledButtonsColumn,
  StyledDividedListElement,
  StyledCurrentCampaignListElement,
  StyledDraggableBoxes,
  StyledCampaignDetailsColumn,
  StyledEligibiltyColumn,
  StyledStartDateColumn,
  StyledDisplayUntilColumn,
  StyledEstimatedSendBox,
  StyledDisplayUntilText,
  StyledSkeleton,
  StyledLink,
  StyledAction,
  StyledOverflowMenu,
  JourneyDetails,
} from './styled/DisplayPriorityOrder.styled';
import {
  DividedList,
  DividedListElement,
  GreyCaption,
} from './styled/DisplayPriorityTakeover.styled';

type DisplayPriorityOrderProps = {
  currentCampaignId?: number;
};

const OrderListElement = ({
  index,
  campaign,
  isCurrentCampaign,
  moveToTop,
  moveToBottom,
  setShowCampaign,
}: {
  index: number;
  campaign: DisplayPriorityCampaign;
  isCurrentCampaign: boolean;
  moveToTop: () => void;
  moveToBottom: () => void;
  setShowCampaign: () => void;
}) => {
  const { location } = useHistory();
  const {
    id,
    name,
    campaignState,
    startAt,
    schedulerMsgParams,
    journey,
    isJourneyAssociated,
    lists,
  } = campaign;
  const campaignNameElementRef = useRef<HTMLDivElement>(null);

  const isPlacementsView = location?.pathname?.includes('settings/placements');

  const defaultFocus = !isPlacementsView ? isCurrentCampaign : index === 0;

  useEffect(() => {
    if (defaultFocus) {
      const elementToFocus = document.getElementById(`draggable-${id}`);
      elementToFocus?.focus();
    }
  }, []);

  const badgeVariant = getCampaignStateBadgeVariant(campaignState);

  const { estimatedSendSizeData, expectedSendSizeData } = useCampaignSendSizeData(
    id,
    lists?.length,
  );
  // This will need to be updated to be computed dynamically from the eligibility service
  const { sendSize, isFetching } = useCampaignSendSize({
    estimatedSendSizeData,
    expectedSendSizeData,
  });

  const isModernizedSegmentationPageEnabled = useIsModernizedSegmentationPageEnabled();

  const options: ActionMenuOption[] = [
    {
      id: 'move-to-highest',
      as: AllowedActionMenuElements.Button,
      content: (
        <StyledAction>
          <ToTop24 />
          Send to highest priority
        </StyledAction>
      ),
      onClick: moveToTop,
    },
    {
      id: 'move-to-lowest',
      as: AllowedActionMenuElements.Button,
      content: (
        <StyledAction>
          <ToBottom24 />
          Send to lowest priority
        </StyledAction>
      ),
      onClick: moveToBottom,
    },
  ];

  const estimatedSendSize = useCallback(() => {
    if (!isFetching && typeof sendSize === 'number')
      return (
        <StyledEstimatedSendBox>
          <User16 />
          <GreyCaption color={colors.textSecondary}>{sendSize} (est)</GreyCaption>
        </StyledEstimatedSendBox>
      );
    if (isFetching)
      return (
        <StyledSkeleton>
          <Skeleton shimmerSpeed={1250} />
        </StyledSkeleton>
      );
    return '';
  }, [isFetching, sendSize]);

  const isCampaignNameTruncated =
    campaignNameElementRef.current &&
    campaignNameElementRef.current?.offsetWidth < campaignNameElementRef.current?.scrollWidth;

  const DisplayToList = !isCurrentCampaign && lists?.length > 0 && (
    <>
      <GreyCaption>
        Display to{' '}
        <StyledLink
          href={`/segmentation?emailListId=${campaign.lists[0].id}`}
          target="_blank"
          useReactRouter={isModernizedSegmentationPageEnabled}
          rel={isModernizedSegmentationPageEnabled ? 'noopener noreferrer' : undefined}
        >
          1 list
        </StyledLink>
      </GreyCaption>
      {estimatedSendSize()}
    </>
  );

  const JourneyEligibility = journey && (
    <>
      <JourneyDetails>{journey.name}</JourneyDetails>
      <GreyCaption>{`Journey ID: ${journey.id}`}</GreyCaption>
    </>
  );

  const eligibilityCampaignStartDate = () => {
    return !isCurrentCampaign && startAt ? moment(startAt).format('MM/DD/YYYY, hh:mma') : '';
  };

  const { Icon, campaignType, eligibilityContent, startDate } = {
    Icon:
      journey || isJourneyAssociated ? (
        <TriggerWorkflow24 height={16} width={16} color={colors.textDisabled} />
      ) : (
        <Eligibility16 color={colors.textDisabled} />
      ),
    campaignType: journey || isJourneyAssociated ? 'Journey triggered' : 'Eligbility',
    eligibilityContent: journey ? JourneyEligibility : DisplayToList,
    startDate: journey
      ? moment(journey?.revisions?.results[0]?.createdAt).format('MM/DD/YYYY, hh:mma')
      : eligibilityCampaignStartDate(),
  };

  const endAt = useMemo(() => {
    if (journey || isJourneyAssociated)
      return getLabelForDisplayUntilForJourneyCampaign(campaign.displayUntil);
    if (schedulerMsgParams?.endDate?.timestamp)
      return `${displayDate(
        stripTimezonePrefix(schedulerMsgParams?.endDate?.timestamp),
        DATE_FORMAT_FULL_WITH_HOURS_MINS,
      )}`;
    if (isCurrentCampaign) return '';
    return 'Campaign is stopped';
  }, [isCurrentCampaign, schedulerMsgParams, journey, isJourneyAssociated]);

  const CampaignDetailsColumn = (
    <StyledCampaignDetailsColumn>
      <StyledImageWrapper>
        <MessageMediumIcon messageMedium={campaign.messageMedium} />
      </StyledImageWrapper>
      <CampaignDetails>
        <Tooltip isEnabled={!!isCampaignNameTruncated} tooltipContent={campaign.name}>
          <BaseDetails ref={campaignNameElementRef}>{name}</BaseDetails>
        </Tooltip>
        <DividedList>
          {isCurrentCampaign ? (
            <StyledCurrentCampaignListElement data-test="display-priority-current-campaign">
              Current campaign
            </StyledCurrentCampaignListElement>
          ) : (
            <>
              <StyledDividedListElement>
                {Icon}
                {campaignType}
              </StyledDividedListElement>
              <DividedListElement>{`ID: ${id}`}</DividedListElement>
              <DividedListElement>
                {<Badge variant={badgeVariant}>{campaignState}</Badge>}
              </DividedListElement>
            </>
          )}
        </DividedList>
      </CampaignDetails>
    </StyledCampaignDetailsColumn>
  );

  const EligibilityColumn = <StyledEligibiltyColumn>{eligibilityContent}</StyledEligibiltyColumn>;

  const DisplayUntilColumn = (
    <StyledDisplayUntilColumn data-canshowenddate={!isCurrentCampaign}>
      <StyledDisplayUntilText>{endAt}</StyledDisplayUntilText>
      <StyledButtonsColumn>
        <Tooltip tooltipContent={!campaign.hidden ? 'Hide from preview' : 'Show in preview'}>
          <IconButton
            onClick={() => setShowCampaign()}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            {!campaign.hidden ? (
              <View16 color={colors.iconPrimary} />
            ) : (
              <Hide16 color={colors.iconPrimary} />
            )}
          </IconButton>
        </Tooltip>
        <StyledOverflowMenu
          size={BUTTON_SIZES.SMALL}
          options={options}
          id={`display-priority-campaign-actions-${campaign.id}`}
        />
      </StyledButtonsColumn>
    </StyledDisplayUntilColumn>
  );

  const StartDateColumn = (
    <StyledStartDateColumn>
      <GreyCaption color={colors.textSecondary}>{startDate || 'Not started'}</GreyCaption>
    </StyledStartDateColumn>
  );

  return (
    <OrderElementWrapper>
      {CampaignDetailsColumn}
      {EligibilityColumn}
      {StartDateColumn}
      {DisplayUntilColumn}
    </OrderElementWrapper>
  );
};

export const DisplayPriorityOrder = ({ currentCampaignId }: DisplayPriorityOrderProps) => {
  const { campaignDisplayPriorityOrder, setCampaignDisplayPriorityOrder, setShowCampaign } =
    useDisplayPriorityContext();

  const handleReorderUpdate = (initialPosition: number, newPosition: number) => {
    const newCampaignOrder = [...campaignDisplayPriorityOrder];
    const campaignToMove = campaignDisplayPriorityOrder[initialPosition];
    newCampaignOrder.splice(initialPosition, 1);
    newCampaignOrder.splice(newPosition, 0, campaignToMove);
    setCampaignDisplayPriorityOrder(newCampaignOrder);
    const elementToFocus = document.getElementById(`draggable-${campaignToMove.id}`);
    elementToFocus?.focus();
  };

  const boxElementConfigs = campaignDisplayPriorityOrder.map((campaign, index) => ({
    text: (
      <OrderListElement
        index={index}
        campaign={campaign}
        isCurrentCampaign={currentCampaignId === campaign.id}
        moveToTop={() => handleReorderUpdate(index, 0)}
        moveToBottom={() => handleReorderUpdate(index, campaignDisplayPriorityOrder.length - 1)}
        setShowCampaign={() => setShowCampaign(index)}
      />
    ),
    id: campaign.id.toString(),
  }));

  return (
    <StyledDraggableBoxes
      allowArrowKeyDragOnFocusedItem
      draggableBoxes={boxElementConfigs}
      leftAlignContent={false}
      onUpdate={handleReorderUpdate}
      withCounter={false}
    />
  );
};
